import { ArrowRightIcon, MoneyAddIcon } from "@xola/icons";
import { almostZero } from "@xola/ui-kit";
import { isArray } from "lodash";
import React from "react";
import { Audit, LineItemType, Purchase, PurchaseItem } from "types";
import { Currency } from "../../../Common/Currency";
import { TimelineItem, TimelineItemReason } from "../TimelineItem";

interface ChangeChargeTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const ChangeChargeTimelineItem = ({
    purchase,
    item,
    audit,
    showDate = false,
}: ChangeChargeTimelineItemProps) => {
    const [oldAmount, newAmount, reason] = getAmount(item, audit);

    if (!ChangeChargeTimelineItem.supportsAudit(purchase, item, audit)) {
        return null;
    }

    return (
        <TimelineItem
            title="Modified Charge"
            audit={audit}
            icon={<MoneyAddIcon />}
            color="success"
            showDate={showDate}
            purchase={purchase}
        >
            <div className="space-y-2">
                <div className="flex items-center space-x-2">
                    <Currency shouldRemoveTrailingZeroes={false}>{oldAmount}</Currency>
                    <ArrowRightIcon />
                    <Currency shouldRemoveTrailingZeroes={false}>{newAmount}</Currency>
                </div>
                <TimelineItemReason className="no-translate">{reason}</TimelineItemReason>
            </div>
        </TimelineItem>
    );
};

ChangeChargeTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    const [oldAmount, newAmount] = getAmount(item, audit);
    return !almostZero(oldAmount) && newAmount;
};

const getAmount = (item: PurchaseItem, audit: Audit): [number, number?, string?] => {
    if (!audit.data?.items) {
        return [0];
    }

    const auditItem =
        isArray(audit.data?.items) &&
        audit.data.items.find((auditItem: any) => auditItem.id === item?.id && auditItem.amount);
    if (!auditItem) {
        return [0];
    }

    const values: any = Object.keys(auditItem.lineItems ?? {});

    for (const value of values) {
        const auditLineItem = auditItem.lineItems[value];

        const lineItemFromPurchaseItem = item.lineItems.find((lineItem) => lineItem.id === auditLineItem.id);

        const isValueAdjustment =
            lineItemFromPurchaseItem?.type === LineItemType.ADJUSTMENT ||
            lineItemFromPurchaseItem?.type === LineItemType.TAXED_ADJUSTMENT;

        if (isValueAdjustment) {
            return [auditLineItem?.amount?.old, auditLineItem?.amount?.new, lineItemFromPurchaseItem?.name];
        }
    }

    return [0];
};
